import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import {
  Flame,
  Plus,
  Trash2,
  CheckCircle2,
  Activity,
  Edit3,
  X,
  Check,
} from 'lucide-react';
import { Habit } from '../../types';
import { getLocalDateString } from '../../lib/dateUtils';

const HABIT_COLORS = [
  '#10b981',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#f97316',
  '#eab308',
  '#06b6d4',
  '#ef4444',
];

export const HabitsView: React.FC = () => {
  const {
    habits,
    habitLogs,
    toggleHabit,
    editHabit,
    deleteHabit,
    setActiveModal,
    t,
  } = useApp();

  const today = new Date();
  const todayStr = getLocalDateString(today);

  // Edit Habit State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingHabit, setEditingHabit] = useState<Habit | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#10b981');
  const [editFrequency, setEditFrequency] = useState<'daily' | 'weekly'>('daily');

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isEditModalOpen) {
        setIsEditModalOpen(false);
        setEditingHabit(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditModalOpen]);

  // Open Edit Modal
  const handleOpenEdit = (habit: Habit) => {
    setEditingHabit(habit);
    setEditName(habit.name);
    setEditColor(habit.color || '#10b981');
    setEditFrequency(habit.frequency || 'daily');
    setIsEditModalOpen(true);
  };

  // Save Edit
  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingHabit || !editName.trim()) return;

    editHabit(editingHabit.id, {
      name: editName.trim(),
      color: editColor,
      frequency: editFrequency,
    });

    setIsEditModalOpen(false);
    setEditingHabit(null);
  };

  // Generate last 7 days keys
  const last7Days = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date();
    d.setDate(today.getDate() - (6 - i));
    const dateStr = getLocalDateString(d);
    const dayLabel = d.toLocaleDateString('en-US', { weekday: 'narrow' });
    return { dateStr, dayLabel, dayNum: d.getDate() };
  });

  // Calculate streak for a habit
  const calculateStreak = (habitId: string) => {
    let streak = 0;
    const checkDate = new Date();

    for (let i = 0; i < 60; i++) {
      const dateStr = getLocalDateString(checkDate);
      const isCompleted = habitLogs.some(
        (hl) => hl.habitId === habitId && hl.date === dateStr && hl.completed
      );
      if (isCompleted) {
        streak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        // If today is not completed yet, check if yesterday was
        if (i === 0) {
          checkDate.setDate(checkDate.getDate() - 1);
          continue;
        }
        break;
      }
    }
    return streak;
  };

  return (
    <div className="space-y-6 animate-fade-in pb-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <Flame className="w-6 h-6 text-amber-500" />
            <span>{t.habitTracker}</span>
          </h1>
        </div>

        <button
          onClick={() => setActiveModal('habit')}
          className="px-4 py-2 bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white rounded-xl text-xs font-bold shadow-md shadow-amber-500/25 flex items-center gap-1.5 transition-all transform active:scale-95 self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>{t.newHabit}</span>
        </button>
      </div>

      {/* Habits Grid */}
      <div className="space-y-4">
        {habits.length === 0 ? (
          <div className="p-12 text-center bg-white dark:bg-[#111827] rounded-3xl border border-slate-200/80 dark:border-slate-800 text-slate-400 text-sm">
            No habits created yet. Click &ldquo;+ New Habit&rdquo; to build your first routine!
          </div>
        ) : (
          habits.map((habit) => {
            const streak = calculateStreak(habit.id);
            const isTodayCompleted = habitLogs.some(
              (hl) => hl.habitId === habit.id && hl.date === todayStr && hl.completed
            );

            return (
              <div
                key={habit.id}
                className="p-6 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs space-y-4 transition-all hover:shadow-md"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  {/* Habit Name & Streak */}
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black shadow-md flex-shrink-0"
                      style={{ backgroundColor: habit.color || '#10b981' }}
                    >
                      <Activity className="w-6 h-6" />
                    </div>

                    <div className="min-w-0">
                      <h3 className="text-base font-extrabold text-slate-900 dark:text-white truncate">
                        {habit.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-0.5 text-xs">
                        <span className="flex items-center gap-1 text-amber-500 font-black">
                          <Flame className="w-3.5 h-3.5 fill-amber-500" />
                          <span>
                            {streak} {t.daysStreak}
                          </span>
                        </span>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-400 capitalize">{habit.frequency}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions: Today Quick Toggle, Edit & Delete */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => toggleHabit(habit.id, todayStr)}
                      className={`px-4 py-2.5 rounded-2xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm ${
                        isTodayCompleted
                          ? 'bg-emerald-500 text-white shadow-emerald-500/20'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-emerald-500/10 hover:text-emerald-500'
                      }`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      <span>{isTodayCompleted ? 'Completed Today!' : t.markToday}</span>
                    </button>

                    {/* Edit Habit Button */}
                    <button
                      onClick={() => handleOpenEdit(habit)}
                      className="p-2 text-slate-400 hover:text-amber-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                      title="Edit Habit"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>

                    {/* Delete Habit Button */}
                    <button
                      onClick={() => deleteHabit(habit.id)}
                      className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 rounded-xl transition-colors"
                      title={t.delete}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* 7-Day Mini Heatmap Row */}
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80">
                  <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
                    <span>Recent 7 Days</span>
                    <span className="text-[11px]">Click day to toggle</span>
                  </div>

                  <div className="grid grid-cols-7 gap-2">
                    {last7Days.map((day) => {
                      const completed = habitLogs.some(
                        (hl) => hl.habitId === habit.id && hl.date === day.dateStr && hl.completed
                      );
                      const isTodaySlot = day.dateStr === todayStr;

                      return (
                        <button
                          key={day.dateStr}
                          onClick={() => toggleHabit(habit.id, day.dateStr)}
                          className={`p-2 rounded-2xl border text-center transition-all flex flex-col items-center justify-center gap-1 ${
                            completed
                              ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-600 dark:text-emerald-400'
                              : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200/60 dark:border-slate-700/60 text-slate-400'
                          } ${isTodaySlot ? 'ring-2 ring-emerald-500/30' : ''}`}
                        >
                          <span className="text-[10px] font-bold uppercase">{day.dayLabel}</span>
                          <span className="text-xs font-black">{day.dayNum}</span>
                          <div
                            className={`w-2 h-2 rounded-full mt-0.5 ${
                              completed ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'
                            }`}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Edit Habit Modal */}
      {isEditModalOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsEditModalOpen(false);
              setEditingHabit(null);
            }
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-lg bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Flame className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Edit Habit</h3>
              </div>
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingHabit(null);
                }}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 uppercase">
                  Habit Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Read 20 pages, 30 min Workout, Drink 3L Water"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-semibold text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Habit Color */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 uppercase">
                  Habit Color
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  {HABIT_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setEditColor(c)}
                      className={`w-7 h-7 rounded-full transition-transform ${
                        editColor === c
                          ? 'scale-125 ring-2 ring-white dark:ring-slate-900 ring-offset-2'
                          : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 uppercase">
                  Frequency
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(['daily', 'weekly'] as const).map((freq) => (
                    <button
                      key={freq}
                      type="button"
                      onClick={() => setEditFrequency(freq)}
                      className={`py-2 rounded-xl text-xs font-bold capitalize border transition-all ${
                        editFrequency === freq
                          ? 'border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400'
                          : 'border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400'
                      }`}
                    >
                      {freq}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingHabit(null);
                  }}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white rounded-xl font-bold text-xs shadow-md shadow-amber-500/25 flex items-center justify-center gap-1.5 transition-all"
                >
                  <Check className="w-4 h-4" />
                  <span>Save Changes</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
